import { useInfiniteQuery } from '@tanstack/react-query';

import { notificationsApi } from '../api/notifications.api';
import { notificationsKeys } from '../api/notifications.keys';
import type { INotification, NotificationType } from '../types';

const PAGE_SIZE = 20;

/**
 * Cursor-paginated, all-notifications list for one tab (used by the "View all"
 * drawer). Mirrors the inbox `useUnifiedListInfinite` pattern. Returns the
 * flattened items plus the controls the infinite-scroll sentinel needs.
 */
export function useNotificationsListInfinite(type: NotificationType, enabled: boolean) {
    const query = useInfiniteQuery({
        queryKey: notificationsKeys.listInfinite(type),
        queryFn: ({ pageParam }) =>
            notificationsApi.list({ type, limit: PAGE_SIZE, cursor: pageParam }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        enabled,
        staleTime: 15_000,
    });

    const items: INotification[] = query.data?.pages.flatMap((page) => page.items) ?? [];

    return {
        items,
        isLoading: query.isLoading,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        fetchNextPage: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                void query.fetchNextPage();
            }
        },
    };
}
